import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import AudioPlayer from '../components/AudioPlayer';
import LoadingSpinner from '../components/LoadingSpinner';
import './PublicAudio.css';

interface AudioData {
  id: number;
  radio: string;
  fileName: string;
  content: string;
  sentimentType: 'positivo' | 'negativo' | 'neutro';
  termo?: string;
  categoria?: string;
  timeStart?: string;
  createdAt: string;
  downloadToken: string;
}

const PublicAudio: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [audioData, setAudioData] = useState<AudioData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAudioData = async () => {
      if (!token) {
        setError('Token não fornecido');
        setLoading(false);
        return;
      }

      try {
        console.log('🔍 Buscando dados do áudio para token:', token.substring(0, 20) + '...');
        
        const response = await fetch(`/api/public/audio/${token}`);
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || 'Erro ao carregar dados do áudio');
        }

        if (!result.success) {
          throw new Error(result.message || 'Resposta inválida do servidor');
        }

        console.log('✅ Dados do áudio carregados:', result.data);
        setAudioData(result.data);
        
      } catch (err: any) {
        console.error('❌ Erro ao carregar áudio:', err);
        setError(err.message || 'Erro desconhecido');
      } finally {
        setLoading(false);
      }
    };

    fetchAudioData();
  }, [token]);

  const getAudioUrl = () => {
    if (!audioData?.downloadToken) return '';
    return `/api/download/${audioData.downloadToken}`;
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positivo':
        return '#4caf50';
      case 'negativo':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case 'positivo':
        return '😊';
      case 'negativo':
        return '😟';
      default:
        return '😐';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="public-audio-page">
        <div className="loading-container">
          <LoadingSpinner />
          <p>Carregando dados do áudio...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-audio-page">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Erro ao Carregar Áudio</h2>
          <p>{error}</p>
          <div className="error-suggestions">
            <h4>Possíveis causas:</h4>
            <ul>
              <li>Link expirado (válido por 3 meses)</li>
              <li>Token inválido ou corrompido</li>
              <li>Áudio removido do sistema</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  if (!audioData) {
    return (
      <div className="public-audio-page">
        <div className="error-container">
          <div className="error-icon">📭</div>
          <h2>Áudio Não Encontrado</h2>
          <p>Os dados do áudio não puderam ser carregados.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-audio-page">
      <div className="audio-container">
        <header className="audio-header">
          <div className="header-content">
            <h1 className="page-title">
              <span className="radio-icon">📻</span>
              Reprodutor de Áudio - {audioData.radio}
            </h1>
            <div className="header-info">
              <span className="file-name">📁 {audioData.fileName}</span>
              <span className="date">📅 {formatDate(audioData.createdAt)}</span>
            </div>
          </div>
        </header>

        <div className="audio-content">
          <div className="analysis-info">
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Sentimento:</span>
                <span 
                  className="sentiment-badge"
                  style={{ 
                    backgroundColor: getSentimentColor(audioData.sentimentType),
                    color: 'white'
                  }}
                >
                  {getSentimentIcon(audioData.sentimentType)} {audioData.sentimentType}
                </span>
              </div>
              
              {audioData.termo && (
                <div className="info-item">
                  <span className="info-label">Termo-chave:</span>
                  <span className="info-value">{audioData.termo}</span>
                </div>
              )}
              
              {audioData.categoria && (
                <div className="info-item">
                  <span className="info-label">Categoria:</span>
                  <span className="info-value">{audioData.categoria}</span>
                </div>
              )}
              
              {audioData.timeStart && (
                <div className="info-item">
                  <span className="info-label">Início da menção:</span>
                  <span className="info-value">
                    ⏰ {audioData.timeStart}
                  </span>
                </div>
              )}
            </div>
            
            <div className="content-section">
              <h3>📝 Conteúdo da Análise:</h3>
              <div className="content-text">
                {audioData.content}
              </div>
            </div>
          </div>

          <div className="player-section">
            <AudioPlayer
              audioUrl={getAudioUrl()}
              timeStart={audioData.timeStart}
              title={`${audioData.radio} - ${audioData.fileName}`}
              onLoadError={(error) => {
                console.error('Erro no player:', error);
                setError(`Erro ao carregar áudio: ${error}`);
              }}
            />
          </div>
        </div>

        <footer className="audio-footer">
          <div className="footer-content">
            <p>
              <span className="info-icon">ℹ️</span>
              Este link é válido por 3 meses a partir da data de geração.
            </p>
            <p className="powered-by">
              Powered by Sistema de Análise de Áudio - Prefeitura de Itajaí
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default PublicAudio;
